import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Vector2 } from 'three';
import { Button, Grid, Stack, Typography } from '@mui/material';
import { Areas } from '@thive/fddm-types';
import { NumUtils } from '@/modules/common/utils';
import { Input } from '@/modules/common/components/Input';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { isHighwayArea } from '@/modules/floorplanDesign/helpers';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { Area } from '@/modules/floorplanDesign/types';
import {
  areasToFlatCollection,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers/helpers';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { IFddmService } from '@/modules/canvas/services/Floorplan/IFddmService';
import { CopyPasteServiceFactory } from '@/modules/copy/service';

type Props = {
  closeModal: () => void;
};

export function MultiCopyModal({ closeModal }: Props) {
  const { t } = useTranslation(['interface', 'common']);
  const { getAreaById } = useFloorplanDesignStore();
  const selectedIds = useAppStore((state) => state.selectedIds);
  const [horizontalCopies, setHorizontalCopies] = useState(0);
  const [verticalCopies, setVerticalCopies] = useState(0);
  const [horizontalSpacing, setHorizontalSpacing] = useState(0);
  const [verticalSpacing, setVerticalSpacing] = useState(0);
  const fddmSvc: IFddmService = useMemo(
    () => FddmServiceFactory.getInstance().createFddmService(),
    [],
  );

  const makeCopies = useCallback(
    (
      shapeId: string,
      horizontalCopiesIn: number,
      verticalCopiesIn: number,
      horizontalSpacingIn: number,
      verticalSpacingIn: number,
    ) => {
      const selectedArea = getAreaById(shapeId);

      // TODO: highways support. different properties need to be taken into account
      // TODO: logical spaces and obstacles support
      if (!selectedArea || isHighwayArea(selectedArea)) return;

      const areaIdsHandled: string[] = [shapeId];
      const newAreas: Areas = {
        rackingAreas: [],
        highwayAreas: [],
        singularStationAreas: [],
        stationaryAreas: [],
        manualAreas: [],
      };

      for (let x = 0; x <= horizontalCopiesIn; x++) {
        for (let y = 0; y <= verticalCopiesIn; y++) {
          if (x === 0 && y === 0) continue;

          const offset = new Vector2(
            (selectedArea.rectangle.length + Math.abs(horizontalSpacingIn)) *
              x *
              Math.sign(horizontalSpacingIn),
            (selectedArea.rectangle.width + Math.abs(verticalSpacingIn)) *
              y *
              Math.sign(verticalSpacingIn),
          );

          const newArea = CopyPasteServiceFactory.getInstance().createService().copyItem<Area>({
            item: selectedArea,
            type: 'Area',
            offset,
          });
          areaIdsHandled.push(newArea.id);
          pushAreaToItsCollection(newAreas, newArea);
        }
      }

      areaIdsHandled.push(...areasToFlatCollection(newAreas).map((area) => area.id));
      fddmSvc.addAreas(newAreas);
    },
    [getAreaById, fddmSvc],
  );

  const handleCopy = useCallback(() => {
    makeCopies(
      selectedIds[0],
      horizontalCopies,
      verticalCopies,
      horizontalSpacing,
      verticalSpacing,
    );

    closeModal();
  }, [
    closeModal,
    horizontalCopies,
    horizontalSpacing,
    makeCopies,
    selectedIds,
    verticalCopies,
    verticalSpacing,
  ]);

  const notNegative = useCallback((input) => Math.max(input, 0), []);

  return (
    <Stack direction='column' spacing={2}>
      <Typography sx={{ fontSize: '14px' }}>
        {t('interface:multi_copy.create_copies', 'Create copies')}
      </Typography>

      <>
        <Grid
          container
          spacing={2}
          columns={13}
          alignItems='center'
          sx={{ width: 320, marginRight: 4 }}
        >
          <Grid item xs={4}>
            <Typography fontSize='10px'>
              {t('interface:multi_copy.direction.horizontal')}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Input
              value={horizontalCopies}
              onChange={(value) => setHorizontalCopies(notNegative(value))}
              mode='positive-integer'
            />
          </Grid>

          <Grid item xs={2}>
            <Typography fontSize='10px'>{t('interface:multi_copy.space_between')}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Input
              value={NumUtils.metersToMmRounded(horizontalSpacing)}
              unit={t(`interface:settings.units.mm`, 'mm')}
              onChange={(value) => setHorizontalSpacing(NumUtils.mmToMetersRoundedToMm(value))}
              mode='integer'
            />
          </Grid>

          <Grid item xs={4}>
            <Typography fontSize='10px'>{t('interface:multi_copy.direction.vertical')}</Typography>
          </Grid>
          <Grid item xs={3}>
            <Input
              value={verticalCopies}
              onChange={(value) => setVerticalCopies(notNegative(value))}
              mode='positive-integer'
            />
          </Grid>

          <Grid item xs={2}>
            <Typography fontSize='10px'>{t('interface:multi_copy.space_between')}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Input
              value={NumUtils.metersToMmRounded(verticalSpacing)}
              unit={t(`interface:settings.units.mm`, 'mm')}
              onChange={(value) => setVerticalSpacing(NumUtils.mmToMetersRoundedToMm(value))}
              mode='integer'
            />
          </Grid>
        </Grid>

        <Stack direction='row' justifyContent='space-between'>
          <Button onClick={handleCopy} disabled={horizontalCopies === 0 && verticalCopies === 0}>
            {t('interface:multi_copy.create_button', 'Create copies')}
          </Button>
          <Button onClick={closeModal}>{t('common:cancel', 'Cancel')}</Button>
        </Stack>
      </>
    </Stack>
  );
}
